<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faCheckCircle, 
    faExclamationTriangle, 
    faChartLine, 
    faClock,
    faCircleCheck,
    faCircleExclamation
  } from '@fortawesome/pro-light-svg-icons';
  import { isCompactMode } from '$stores/uiStore';

  export let stats = [];
  export let selectedRow = null;
  export let baselineRow = null;
  export let studyProperties = null;

  const defaultThresholds = {
    veryLow: 54,
    low: 70,
    high: 180,
    veryHigh: 250
  };

  $: thresholds = {
    veryLow: studyProperties?.glucoseParameters?.thresholds?.veryLow ?? defaultThresholds.veryLow,
    low: studyProperties?.glucoseParameters?.thresholds?.low ?? defaultThresholds.low,
    high: studyProperties?.glucoseParameters?.thresholds?.high ?? defaultThresholds.high,
    veryHigh: studyProperties?.glucoseParameters?.thresholds?.veryHigh ?? defaultThresholds.veryHigh
  };

  // Get the selected stat or the first stat if none selected
  $: currentStat = selectedRow || (stats.length > 0 ? stats[0] : null);

  // Helper function to determine if CV is in good range (<36%)
  function isGoodCV(cv) {
    return cv < 36;
  }

  // Helper function to determine if GMI change is positive
  function isPositiveGMIChange(gmi) {
    return gmi < 0;
  }

  // Helper function to determine if data completeness is good
  function isGoodCompleteness(completeness) {
    return completeness >= 90;
  }

  // Helper function to determine if a change is positive
  function isPositiveChange(oldValue, newValue) {
    // For GMI and CV%, lower is better
    if (oldValue === baselineRow?.gmi || oldValue === baselineRow?.cvPercent) {
      return newValue < oldValue;
    }
    // For TIR, higher is better
    if (oldValue === baselineRow?.target?.percent) {
      return newValue > oldValue;
    }
    // For hypo events, lower is better
    if (oldValue === baselineRow?.hypoEvents) {
      return newValue < oldValue;
    }
    return false;
  }

  // Helper function to format change with arrow and checkmark
  function formatChange(oldValue, newValue, threshold = 15) {
    const isPositive = isPositiveChange(oldValue, newValue);
    const percentChange = Math.abs((newValue - oldValue) / oldValue * 100);
    
    let arrow = '';
    if (oldValue !== newValue) {
      if (percentChange <= threshold) {
        arrow = oldValue > newValue ? '↘' : '↗';
      } else {
        arrow = oldValue > newValue ? '↓' : '↑';
      }
    }
    
    return `${oldValue} → ${newValue} <span class="${isPositive ? 'text-green-500' : 'text-red-500'}">${arrow}</span>`;
  }
</script>

{#if currentStat}
  <div class="grid grid-cols-5 gap-4 px-1 pt-3">
    {#if baselineRow}
      <!-- Comparison Cards -->
      <!-- TIR Comparison -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveChange(baselineRow.target.percent, currentStat.target.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isPositiveChange(baselineRow.target.percent, currentStat.target.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isPositiveChange(baselineRow.target.percent, currentStat.target.percent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">TIR%</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{@html formatChange(baselineRow.target.percent, currentStat.target.percent, 10)}</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Time in Range ({thresholds.low}-{thresholds.high} mg/dL)</p>
        {/if}
      </div>

      <!-- GMI Comparison -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveChange(baselineRow.gmi, currentStat.gmi) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faChartLine} 
            class="text-xl {isPositiveChange(baselineRow.gmi, currentStat.gmi) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">GMI%</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{@html formatChange(baselineRow.gmi, currentStat.gmi, 10)}</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Estimated HbA1c</p>
        {/if}
      </div>

      <!-- CV% Comparison -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveChange(baselineRow.cvPercent, currentStat.cvPercent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isPositiveChange(baselineRow.cvPercent, currentStat.cvPercent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isPositiveChange(baselineRow.cvPercent, currentStat.cvPercent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">CV%</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{@html formatChange(baselineRow.cvPercent, currentStat.cvPercent, 10)}</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Glucose Variability</p>
        {/if}
      </div>

      <!-- Hypo Events Comparison -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveChange(baselineRow.hypoEvents, currentStat.hypoEvents) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            class="text-xl text-yellow-500 flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Hypo Events</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{@html formatChange(baselineRow.hypoEvents, currentStat.hypoEvents, 20)}</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Events &gt;{(studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} min below range</p>
        {/if}
      </div>

      <!-- Hyper Events Comparison -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveChange(baselineRow.hyperEvents, currentStat.hyperEvents) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            class="text-xl text-yellow-500 flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Hyper Events</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{@html formatChange(baselineRow.hyperEvents, currentStat.hyperEvents, 20)}</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Events &gt;{(studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} min above range</p>
        {/if}
      </div>
    {:else}
      <!-- Original Single Row Cards -->
      <!-- CV Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodCV(currentStat.cvPercent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodCV(currentStat.cvPercent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodCV(currentStat.cvPercent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Glucose Stability</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{currentStat.cvPercent}% CV</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Target: &lt;36%</p>
        {/if}
      </div>

      <!-- TIR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {currentStat.target.percent >= 70 ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={currentStat.target.percent >= 70 ? faCircleCheck : faCircleExclamation} 
            class="text-xl {currentStat.target.percent >= 70 ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Time in Range</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{currentStat.target.percent}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Target: &gt;70% ({thresholds.low}-{thresholds.high} mg/dL)</p>
        {/if}
      </div>

      <!-- TAR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {(currentStat.high.percent + currentStat.veryHigh.percent) <= 25 ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={(currentStat.high.percent + currentStat.veryHigh.percent) <= 25 ? faCircleCheck : faCircleExclamation} 
            class="text-xl {(currentStat.high.percent + currentStat.veryHigh.percent) <= 25 ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Time Above Range</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(currentStat.high.percent + currentStat.veryHigh.percent).toFixed(1)}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Target: &lt;25% (&gt;{thresholds.high} mg/dL)</p>
        {/if}
      </div>

      <!-- TBR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {(currentStat.low.percent + currentStat.veryLow.percent) <= 4 ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={(currentStat.low.percent + currentStat.veryLow.percent) <= 4 ? faCircleCheck : faCircleExclamation} 
            class="text-xl {(currentStat.low.percent + currentStat.veryLow.percent) <= 4 ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Time Below Range</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(currentStat.low.percent + currentStat.veryLow.percent).toFixed(1)}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Target: &lt;4% (&lt;{thresholds.low} mg/dL)</p>
        {/if}
      </div>

      <!-- GMI Card -->
      <!-- <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isPositiveGMIChange(currentStat.gmi) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faChartLine} 
            class="text-xl {isPositiveGMIChange(currentStat.gmi) ? 'text-green-500' : 'text-yellow-500'}"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">GMI</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{currentStat.gmi}%</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Estimated HbA1c</p>
      </div> -->

      <!-- Data Completeness Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodCompleteness(currentStat.completenessPercent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faClock} 
            class="text-xl {isGoodCompleteness(currentStat.completenessPercent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Data Quality</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{currentStat.completenessPercent}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">of expected readings</p>
        {/if}
      </div>
    {/if}
  </div>
{/if} 